import React, { useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { yupResolver } from '@hookform/resolvers'
import { ISelectOption } from 'components/forms/Selects'
import isEmpty from 'lodash/isEmpty'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { DeprecatedControlledSelect } from 'transfix-form/components/ControlledSelect'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Box } from 'transfix-ui/core'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import * as yup from 'yup'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useTeamMembersApi } from 'apps/fleet-planner/hooks/useTeamMembersApi'
import {
  ICarrier,
  IMotorCarrier,
  ITeamMember,
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
} from 'apps/fleet-planner/types'
import { selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { ToastState } from 'transfix-ui'
import { AddEntityModal } from './AddEntityModal'

const USER_TYPE_OPTIONS = [
  {
    label: 'Dispatcher',
    value: TEAM_MEMBER_USER_TYPES.DISPATCHER,
  },
  {
    label: 'Owner Operator',
    value: TEAM_MEMBER_USER_TYPES.OWNER_OPERATOR,
  },
]

const ROLE_OPTIONS = [
  {
    label: 'Admin',
    value: TEAM_MEMBER_ROLE_TYPES.ADMIN,
  },
  {
    label: 'Employee',
    value: TEAM_MEMBER_ROLE_TYPES.EMPLOYEE,
  },
]

const DispatcherTeamMemberSchema = yup.object<ITeamMember>().shape({
  firstName: yup.string().required('Please enter a first name.'),
  lastName: yup.string().required('Please enter a last name.'),
  phone: yup
    .string()
    .required('Please enter a phone number.')
    .test('len', 'Please enter a valid phone number.', val => val?.length === 10),
  email: yup.string().email().required('Please enter an email.'),
  cellPhone: yup.string().when('userType', {
    is: 'owner_operator',
    then: yup
      .string()
      .required('Please enter a cellphone number.')
      .test('len', 'Please enter a valid cellphone number.', val => val.length === 10),
    otherwise: yup.string(),
  }),
  carrierId: yup.number().required('Please select a carrier'),
  role: yup.mixed<TEAM_MEMBER_ROLE_TYPES>().required('Please select an option'),
  userType: yup.mixed<TEAM_MEMBER_USER_TYPES>().required('Please select an option'),
})

type DispatcherTeamMemberSchemaType = yup.InferType<typeof DispatcherTeamMemberSchema>

type IAddDispatcherModalProps = {
  selectedCarrier?: ICarrier | IMotorCarrier
  onClose: () => void
  onSubmit: (
    toastState: Omit<ToastState, 'toastOpen'>,
    teamMember?: Omit<ITeamMember, 'motorCarrierDetails'>
  ) => void
}

export const AddDispatcherModal = ({
  onClose,
  onSubmit,
  selectedCarrier,
}: IAddDispatcherModalProps) => {
  const { trackEvent } = useFMSAnalytics()
  const { isMobile } = useBreakpointContext()
  const { createTeamMember } = useTeamMembersApi()
  const carriers = useSelector(selectCarriersList)

  const { control, errors, handleSubmit, setError } = useForm<DispatcherTeamMemberSchemaType>({
    mode: 'onBlur',
    resolver: yupResolver(DispatcherTeamMemberSchema),
  })

  const carrierSelectOptions: ISelectOption[] = useMemo(
    () => carriers.map(carrier => ({ label: carrier.legalName || '', value: carrier.id })),
    [carriers]
  )

  const currentRole = useWatch<TEAM_MEMBER_ROLE_TYPES | undefined>({
    name: 'role',
    control,
    defaultValue: undefined,
  })

  const currentUserType = useWatch<TEAM_MEMBER_USER_TYPES | undefined>({
    name: 'userType',
    control,
    defaultValue: undefined,
  })

  const requireCellphone = useMemo(() => currentUserType === 'owner_operator', [currentUserType])

  const roleMessage = useMemo(() => {
    if (errors?.role?.message) {
      return errors.role.message
    }
    if (currentRole === 'admin') {
      return 'Admins can invite new team members to a carrier and view all of their carrier’s information.'
    }
    if (currentRole === 'employee') {
      return 'Employees can view all of their carrier’s information but can’t invite new team members.'
    }
    return ''
  }, [currentRole, errors?.role?.message])

  const userTypeMessage = useMemo(() => {
    if (errors?.userType?.message) {
      return errors.userType.message
    }
    if (currentUserType === 'dispatcher') {
      return 'Dispatchers can view and manage all of their carrier’s loads, and can only be assigned to loads as a Dispatcher.'
    }
    if (currentUserType === 'owner_operator') {
      return 'Owner Operators can view and manage all of their carrier’s loads, and can be assigned to loads as a Dispatcher or Driver.'
    }
    return ''
  }, [currentUserType, errors?.userType?.message])

  const submitHandler = async (formData: DispatcherTeamMemberSchemaType) => {
    const payload: RecursivePartial<ITeamMember> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      cellPhone: formData.cellPhone,
      phone: formData.phone,
      email: formData.email,
      motorCarrierDetails: [
        {
          role: formData.role,
          userType: formData.userType,
          motorCarrierId: formData.carrierId,
        },
      ],
    }
    let createdTeamMember
    let toastMessage
    try {
      createdTeamMember = await createTeamMember(payload)
      const { firstName = '', lastName = '' } = createdTeamMember || {}
      const fullNameString = firstName && lastName ? ` (“${firstName} ${lastName}”)` : ''
      toastMessage = createdTeamMember?.isExternal
        ? `Your team member already has a Transfix account${fullNameString}. They’ve been added to your carrier.`
        : 'dispatcher added.'
    } catch (err) {
      toastMessage = err || 'An unexpected error occurred.'
    }
    if (
      toastMessage ===
      'Error: cannot add a team member with the email or cellphone number of an existing one'
    ) {
      setError('cellPhone', {
        type: 'manual',
        shouldFocus: true,
        message: 'This cellphone number is already in use. Please enter another one.',
      })
    } else {
      onSubmit(
        {
          toastType: createdTeamMember ? 'default' : 'alert',
          toastMessage,
        },
        createdTeamMember
      )
      if (createdTeamMember) {
        trackEvent('Team Management', 'TEAM_MEMBER_ADD_SUCCESS')
      } else {
        trackEvent('Team Management', 'TEAM_MEMBER_ADD_ERROR')
      }
      onClose()
    }
  }

  return (
    <AddEntityModal
      title='Add New Dispatcher'
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          action: onClose,
          dataTest: 'add-dispatcher-modal-cancel-button',
        },
        {
          label: 'Add Dispatcher',
          type: 'confirm',
          action: handleSubmit(submitHandler),
          dataTest: 'add-dispatcher-modal-submit-button',
        },
      ]}
      open
      onClose={onClose}
      dataTest='add-dispatcher-modal'
      error={isEmpty(errors) ? undefined : 'Please fill out the required fields below.'}>
      {/* First Name */}
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='firstName'
          TextInputProps={{
            placeholder: 'John',
            label: 'First Name',
            large: isMobile,
            required: true,
            autoFocus: true,
            validationMessage: errors?.firstName?.message,
            invalid: !!errors?.firstName?.message,
            dataTest: 'add-dispatcher-modal-firstName-input',
          }}
        />
      </Box>
      {/* Last Name */}
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='lastName'
          TextInputProps={{
            placeholder: 'Doe',
            label: 'Last Name',
            large: isMobile,
            required: true,
            validationMessage: errors?.lastName?.message,
            invalid: !!errors?.lastName?.message,
            dataTest: 'add-dispatcher-modal-lastName-input',
          }}
        />
      </Box>
      {/* Email */}
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='email'
          TextInputProps={{
            placeholder: 'johnwayne@example.com',
            label: 'Email',
            large: isMobile,
            validationMessage: errors?.email?.message,
            invalid: !!errors?.email?.message,
            dataTest: 'add-dispatcher-modal-email-input',
            helperMessage: 'This is the email your team member will use to log in to Transfix.',
            className: 'fs-mask',
          }}
        />
      </Box>
      {/* Phone */}
      <Box mb={2}>
        <ControlledPhoneInput
          control={control}
          name='phone'
          PhoneInputProps={{
            placeholder: '(555) 123-4567',
            label: 'Phone',
            large: isMobile,
            required: true,
            invalid: !!errors?.phone?.message,
            dataTest: 'add-dispatcher-modal-phone-input',
            validationMessage:
              errors?.phone?.message ||
              'This phone number will be shown to customers only when this team member is assigned to the customer’s load.',
            className: 'fs-mask',
          }}
        />
      </Box>
      {/* Cellphone */}
      <Box mb={2}>
        <ControlledPhoneInput
          control={control}
          name='cellPhone'
          PhoneInputProps={{
            placeholder: '(555) 123-4567',
            label: requireCellphone ? 'Cell Phone' : 'Cell Phone (Optional)',
            large: isMobile,
            invalid: !!errors?.cellPhone?.message,
            validationMessage: errors?.cellPhone?.message,
            dataTest: 'add-dispatcher-modal-cellphone-input',
            required: requireCellphone,
            className: 'fs-mask',
          }}
        />
      </Box>
      <Box mb={2}>
        <h3>Carrier Information</h3>
      </Box>
      {/* Carrier */}
      <Box mb={2}>
        <DeprecatedControlledSelect
          control={control}
          name='carrierId'
          defaultValue={
            carrierSelectOptions.find(option => option.value === selectedCarrier?.id)?.value
          }
          SelectProps={{
            required: true,
            label: 'Carrier',
            large: isMobile,
            options: carrierSelectOptions,
            disabled: !!selectedCarrier,
            dataTest: 'add-dispatcher-modal-carrier-select',
          }}
        />
      </Box>
      {/* User Type Select */}
      <Box mb={2}>
        <DeprecatedControlledSelect
          control={control}
          defaultValue={null}
          name='userType'
          SelectProps={{
            required: true,
            label: 'User Type',
            large: isMobile,
            options: [...USER_TYPE_OPTIONS],
            placeholder: 'Select an option',
            validationMessage: userTypeMessage,
            invalid: !!errors?.userType?.message,
            dataTest: 'add-dispatcher-modal-user-type-select',
          }}
        />
      </Box>
      {/* Role Select */}
      <Box mb={2}>
        <DeprecatedControlledSelect
          control={control}
          defaultValue={null}
          name='role'
          SelectProps={{
            required: true,
            label: 'Role',
            large: isMobile,
            options: [...ROLE_OPTIONS],
            placeholder: 'Select an option',
            validationMessage: roleMessage,
            invalid: !!errors?.role?.message,
            dataTest: 'add-dispatcher-modal-role-select',
          }}
        />
      </Box>
    </AddEntityModal>
  )
}
